'use client';
import type React from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { useRovingRadio } from '@cineamo/composables/src/hooks/useRovingRadio';

export type GlassSegmentOption<T extends string> = { value: T; label: string };

/**
 * One choice out of a few, in the dark glass of the seat map controls: readable over light and dark rooms alike.
 * Each option is a tab stop unless `roving` makes the group one tab stop whose arrow keys move and choose.
 * Long groups wrap instead of overflowing narrow phones.
 */
export default function GlassSegmentedControl<T extends string>({
    options,
    value,
    onChange,
    label,
    caption,
    roving = false,
    className
}: {
    options: GlassSegmentOption<T>[];
    /** The chosen option, or `null` while none of them is. */
    value: T | null;
    onChange: (value: T) => void;
    /** Accessible name of the group. */
    label: string;
    /** Visible caption beside the group; screen readers hear `label` instead. */
    caption?: string;
    /** One tab stop, arrow keys choose. Off by default: every arrow press would be a choice (and its tracking). */
    roving?: boolean;
    className?: string;
}): React.JSX.Element {
    const choose = (next: T) => {
        if (next !== value) onChange(next);
    };
    const { groupProps, tabbableIndex } = useRovingRadio({
        count: options.length,
        selectedIndex: options.findIndex((option) => option.value === value),
        onSelect: (index) => choose(options[index].value)
    });
    return (
        <div className={cn('flex items-center gap-6', className)}>
            {caption && (
                <span
                    aria-hidden="true"
                    className="rounded-4 bg-black/50 px-8 py-4 text-body-bold-14 text-white backdrop-blur-[6px]">
                    {caption}
                </span>
            )}
            <div
                {...(roving ? groupProps : {})}
                role="radiogroup"
                aria-label={label}
                className="flex flex-wrap items-center rounded-[6px] bg-black/50 p-2 backdrop-blur-[6px]">
                {options.map((option, index) => (
                    <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={option.value === value}
                        tabIndex={roving ? (index === tabbableIndex ? 0 : -1) : undefined}
                        onClick={() => choose(option.value)}
                        className={cn(
                            'rounded-4 px-12 py-4 text-body-bold-12 outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)]',
                            option.value === value
                                ? 'bg-white text-darkBlue shadow-[0_3px_4px_rgba(0,0,0,0.12),0_3px_0.5px_rgba(0,0,0,0.04)]'
                                : 'text-white'
                        )}>
                        {option.label}
                    </button>
                ))}
            </div>
        </div>
    );
}
